import { VoucherClass } from "./enums";
/**
 * The Simple Accounting experience layer.
 *
 * This file defines the *policy* — who sees which mode, what business language
 * replaces which accounting term, which everyday action maps to which voucher
 * class, and when a transaction needs someone's approval.
 *
 * What it deliberately does NOT do is change accounting. There is one set of
 * books, one engine, one chart of accounts. Simple Mode is a different way of
 * asking the same questions and a different vocabulary for the same records; an
 * entry made in Simple Mode and the same entry typed by an accountant are
 * indistinguishable once posted, because they go through the same voucher API.
 *
 * Keeping the policy here, in shared, means the API and the web app cannot
 * drift: the mode a server grants and the mode a browser renders come from one
 * definition.
 */
export const AccountingMode = {
    /** Business language. No debits, credits, ledgers or voucher types. */
    SIMPLE: "SIMPLE",
    /** The existing accounting interface, unchanged. */
    ACCOUNTANT: "ACCOUNTANT",
};
/**
 * Which mode a role lands in when it signs in.
 *
 * This is a default, not a cage. Anyone who is *allowed* Accountant Mode may
 * switch to it (see `canUseAccountantMode`), and the switch is remembered per
 * user. The point of the default is that a site manager should never have to
 * learn what a contra voucher is to record a transfer, and an accountant should
 * never have to click past a simplified screen to reach the trial balance.
 */
export const DEFAULT_MODE_BY_ROLE = {
    SUPER_ADMIN: AccountingMode.ACCOUNTANT,
    COMPANY_ADMIN: AccountingMode.ACCOUNTANT,
    // A director wants the business picture first, and can switch when they want
    // the detail behind it.
    DIRECTOR: AccountingMode.SIMPLE,
    FINANCE_MANAGER: AccountingMode.ACCOUNTANT,
    ACCOUNTANT: AccountingMode.ACCOUNTANT,
    AUDITOR: AccountingMode.ACCOUNTANT,
    PROJECT_MANAGER: AccountingMode.SIMPLE,
    PURCHASE_MANAGER: AccountingMode.SIMPLE,
    STORE_MANAGER: AccountingMode.SIMPLE,
    VIEWER: AccountingMode.SIMPLE,
};
/**
 * Who may see the accounting interface at all.
 *
 * Not a secrecy control — it is about not putting a screen full of debits in
 * front of someone whose job never needs it. An auditor and a director need it;
 * a store manager does not, and showing it to them only invites mistakes.
 */
const ACCOUNTANT_MODE_ROLES = new Set([
    "SUPER_ADMIN",
    "COMPANY_ADMIN",
    "DIRECTOR",
    "FINANCE_MANAGER",
    "ACCOUNTANT",
    "AUDITOR",
]);
export function canUseAccountantMode(roles) {
    return roles.some((role) => ACCOUNTANT_MODE_ROLES.has(role));
}
export function defaultModeFor(roles) {
    // The most technical role a person holds decides where they start.
    return roles.some((role) => DEFAULT_MODE_BY_ROLE[role] === AccountingMode.ACCOUNTANT)
        ? AccountingMode.ACCOUNTANT
        : AccountingMode.SIMPLE;
}
/**
 * Business language for accounting terms (spec §11).
 *
 * UI labels only. Nothing here renames a database column, a ledger, a voucher
 * class or an API field — an accountant and a site manager are looking at the
 * same record through different words.
 */
export const SIMPLE_TERMS = {
    ledger: "Account",
    ledgerGroup: "Account type",
    voucher: "Transaction",
    journal: "Adjustment",
    contra: "Transfer between our accounts",
    sundryCreditors: "Suppliers & contractors",
    sundryDebtors: "Customers who owe us",
    debit: "Money movement",
    credit: "Money movement",
    trialBalance: "Account balances",
    narration: "Note",
    voucherClass: "What happened",
    costCentre: "Project",
    costCategory: "Spent on",
    party: "Person or company",
    receivable: "Money owed to us",
    payable: "Money we owe",
};
export const SIMPLE_ACTIONS = [
    {
        key: "paid",
        icon: "💰",
        label: "I paid money",
        prompt: "Who did you pay, how much, and what for?",
        placeholder: "Paid ₹50,000 to ABC Electricals for wiring at Bruhat Ahana from HDFC Bank",
        voucherClass: VoucherClass.PAYMENT,
        requires: "voucher:create",
    },
    {
        key: "received",
        icon: "💵",
        label: "I received money",
        prompt: "Who paid you, how much, and what for?",
        placeholder: "Received ₹45,000 rental income into HDFC Bank",
        voucherClass: VoucherClass.RECEIPT,
        requires: "voucher:create",
    },
    {
        key: "bought",
        icon: "🧾",
        label: "I bought something",
        prompt: "What did you buy, from whom, and for how much?",
        placeholder: "Bought cement worth ₹80,000 from Sri Sai Traders for Bruhat Ahana",
        voucherClass: VoucherClass.MATERIAL_PURCHASE,
        requires: "voucher:create",
    },
    {
        key: "customer-paid",
        icon: "🏠",
        label: "Customer paid",
        prompt: "Which customer paid, how much, and against which unit?",
        placeholder: "Ravi paid ₹10 lakh booking amount for A-1203 at Bruhat Ahana",
        voucherClass: VoucherClass.CUSTOMER_RECEIPT,
        requires: "receipt:create",
    },
    {
        key: "contractor",
        icon: "👷",
        label: "Contractor payment",
        prompt: "Which contractor, how much, and for what work?",
        placeholder: "Paid ₹2,50,000 to Srinivas Constructions for civil work at Bruhat Ahana",
        voucherClass: VoucherClass.PAYMENT,
        requires: "voucher:create",
    },
    {
        key: "salary",
        icon: "👨‍💼",
        label: "Salary / staff payment",
        prompt: "How much salary was paid, and from which account?",
        placeholder: "Paid salary ₹4 lakh from HDFC Bank",
        voucherClass: VoucherClass.PAYMENT,
        requires: "voucher:create",
    },
    {
        key: "transfer",
        icon: "🔄",
        label: "Transfer between accounts",
        prompt: "Which account did the money move from, and to where?",
        placeholder: "Transferred ₹10 lakh from HDFC Bank to ICICI Bank",
        voucherClass: VoucherClass.CONTRA,
        requires: "banking:create",
    },
    {
        key: "other",
        icon: "📄",
        label: "Other transaction",
        prompt: "Describe what happened.",
        placeholder: "Paid ₹35,000 office rent from HDFC Bank",
        voucherClass: null,
        requires: "voucher:create",
    },
];
export const SIMPLE_APPROVAL_POLICY = {
    /*
      A director is deliberately absent. The existing permission matrix gives
      DIRECTOR `approve` but not `create`, separating the person who authorises
      spending from the person who records it — and Simple Mode is not a reason to
      collapse that. A director gets the business dashboard and the approval
      queue; someone else types the transaction.
    */
    canRecord: [
        "SUPER_ADMIN",
        "COMPANY_ADMIN",
        "FINANCE_MANAGER",
        "ACCOUNTANT",
        "PROJECT_MANAGER",
        "PURCHASE_MANAGER",
        "STORE_MANAGER",
    ],
    recordsAsDraft: ["PROJECT_MANAGER", "PURCHASE_MANAGER", "STORE_MANAGER"],
    canPostDirectly: [
        "SUPER_ADMIN",
        "COMPANY_ADMIN",
        "FINANCE_MANAGER",
        "ACCOUNTANT",
    ],
    recommendedThresholds: {
        /** A payment above this goes to a second approver. */
        PAYMENT: 100_000,
        /** Bank-to-bank transfers move real money and are easy to mistype. */
        BANK_TRANSFER: 500_000,
        /** Customer receipts are reconciled against a demand, so a higher bar. */
        CUSTOMER_RECEIPT: 1_000_000,
        PURCHASE: 250_000,
    },
};
/** True when this role's Simple-Mode entries should be saved as a draft. */
export function recordsAsDraft(roles) {
    // If a person holds any role that can post directly, they post directly.
    const canPost = roles.some((r) => SIMPLE_APPROVAL_POLICY.canPostDirectly.includes(r));
    if (canPost)
        return false;
    return roles.some((r) => SIMPLE_APPROVAL_POLICY.recordsAsDraft.includes(r));
}
/**
 * Group the receivables worklist by customer (spec §20).
 *
 * The endpoint answers per BOOKING, which is the right shape for receivables
 * and the wrong shape for "who owes me": §20 lists people — "Ravi ₹12L" — and
 * a customer who has bought two flats would otherwise appear twice, with
 * neither line being what they owe.
 *
 * Nothing is dropped. The units come along, and so do their projects, because
 * naming one project beside units spread across two would put the wrong site
 * against half of them.
 */
export function groupOutstandingByCustomer(rows) {
    const grouped = new Map();
    for (const row of rows) {
        const existing = grouped.get(row.partyId);
        if (existing) {
            existing.total += Number(row.outstanding);
            existing.overdueDays = Math.max(existing.overdueDays, row.overdueDays);
            existing.overdueDemands += row.overdueDemands;
            if (!existing.unitNumbers.includes(row.unitNumber)) {
                existing.unitNumbers.push(row.unitNumber);
            }
            if (!existing.projectNames.includes(row.projectName)) {
                existing.projectNames.push(row.projectName);
            }
            continue;
        }
        grouped.set(row.partyId, {
            partyId: row.partyId,
            partyName: row.partyName,
            phone: row.phone ?? null,
            outstanding: "0.00",
            total: Number(row.outstanding),
            overdueDays: row.overdueDays,
            overdueDemands: row.overdueDemands,
            unitNumbers: [row.unitNumber],
            projectNames: [row.projectName],
        });
    }
    return [...grouped.values()]
        .map(({ total, ...customer }) => ({
        ...customer,
        outstanding: total.toFixed(2),
    }))
        // Most overdue first: the screen answers "who do I chase" before "who owes
        // most", because a small debt six months late is the more urgent call.
        .sort((a, b) => b.overdueDays - a.overdueDays ||
        Number(b.outstanding) - Number(a.outstanding));
}
//# sourceMappingURL=simple-accounting.js.map